import { SOCIAL_MEDIA_LIST } from '@adalo/constants'
import { evaluateBinding, getConditionValue } from './dependencies'
import { formatLibraryValue } from './library-values'
import { normalizeStyles } from './styles'
import { getValue } from '../ducks/formInputs'
import socialMediaManifest from '../components/SocialMediaList/manifest.json'

export function solveBindings(app, object, context) {
  const {
    getBinding,
    getAssetURL,
    getImageUploadsBaseURL,
    getFileUploadsBaseURL,
    state,
  } = context

  const { libraryName, componentName } = object.attributes

  let manifest =
    app.libraryComponentManifests?.[libraryName]?.[componentName] ||
    object.libraryComponentManifest

  if (object.type === SOCIAL_MEDIA_LIST) {
    manifest = socialMediaManifest
  }

  if (!manifest) {
    return {}
  }

  const values = object.attributes
  const allGlobals = (app && app.libraryGlobals) || {}

  const libraryGlobals =
    (allGlobals[libraryName] && allGlobals[libraryName][componentName]) || {}

  const result = {}

  const childComponents = manifest.childComponents || []
  const normalChildComponents = []
  const references = {}
  const normalProps = []
  const referenceProps = {}

  for (const child of childComponents) {
    if (hasRole(child, 'listItem') && child.reference) {
      references[child.reference] = references[child.reference] || []
      references[child.reference].push(child)
    } else {
      normalChildComponents.push(child)
    }
  }

  for (const prop of manifest.props) {
    if (hasRole(prop, 'listItem') && prop.reference) {
      referenceProps[prop.reference] = referenceProps[prop.reference] || []
      referenceProps[prop.reference].push(prop)
    } else {
      normalProps.push(prop)
    }
  }

  const bindingValues = {}

  // TODO: should something happen here for images
  for (const prop of normalProps) {
    const value = prop.global ? libraryGlobals[prop.name] : values[prop.name]

    let formattedValue

    if (value) {
      let val = evaluateBinding(state, value, {
        ...context,
        listItemId: values.listItemId,
      })

      if (prop.type === 'condition') {
        val = getConditionValue(val, value, { ...context, getBinding, state })
      }

      if (prop.type === 'object' && typeof value === 'object' && !val) {
        val = value
      }

      if (!val && val !== 0 && val !== false && val !== '') {
        if (
          value.type === 'imageBinding' &&
          value.options &&
          value.options.placeholderImageEnabled &&
          value.options.placeholderImage
        ) {
          formattedValue = getAssetURL(value.options.placeholderImage)
        } else {
          continue
        }
      }

      if (Array.isArray(val)) {
        // Map list items
        val = val.map(itm => {
          const childProps = {}

          const getListBinding = listId => {
            if (listId === value.id) {
              return itm
            }

            if (getBinding) {
              return getBinding(listId)
            }
          }

          const refProps = referenceProps[prop.name] || []

          for (const prop of refProps) {
            let value = values[prop.name]
            let binding

            if (value) {
              if (value.filename1x) {
                value = value.filename1x
              } else {
                binding = value

                value = evaluateBinding(state, value, {
                  ...context,
                  getBinding: getListBinding,
                  listItemId: itm.id,
                })
              }
            }

            childProps[prop.name] = formatLibraryValue(value, prop, binding, {
              getAssetURL,
              getImageUploadsBaseURL,
              getFileUploadsBaseURL,
              values,
              object,
            })
          }

          const refs = references[prop.name] || []

          for (const child of refs) {
            const props = {}

            for (const prop of child.props) {
              const childValues = values[child.name]
              let value = childValues && childValues[prop.name]
              const binding = value

              if (value) {
                value = evaluateBinding(state, value, {
                  ...context,
                  getBinding: getListBinding,
                  listItemId: itm.id,
                })
              }

              props[prop.name] = formatLibraryValue(value, prop, binding, {
                getAssetURL,
                getImageUploadsBaseURL,
                getFileUploadsBaseURL,
                values: childValues,
                object,
              })

              if (prop.styles) {
                if (!props.styles) {
                  props.styles = {}
                }
                props.styles[prop.name] = normalizeStyles(
                  prop,
                  values[child.name],
                  app.branding
                )
              }
            }

            childProps[child.name] = props
          }

          childProps.id = (itm._meta && itm._meta.id) || itm.id
          childProps._meta = { ...itm._meta, record: itm }
          return childProps
        })

        bindingValues[value.id] = val
      }

      formattedValue = formatLibraryValue(val, prop, value, {
        getAssetURL,
        getImageUploadsBaseURL,
        getFileUploadsBaseURL,
        values,
        object,
      })
    } else {
      formattedValue = formatLibraryValue(value, prop, null, {
        getAssetURL,
        getImageUploadsBaseURL,
        getFileUploadsBaseURL,
        values,
        object,
      })
    }

    if (
      !formattedValue &&
      value &&
      value.type === 'imageBinding' &&
      value.options &&
      value.options.placeholderImageEnabled &&
      value.options.placeholderImage
    ) {
      formattedValue = getAssetURL(value.options.placeholderImage)
    }

    if (hasRole(prop, 'formValue')) {
      result[prop.name] = {
        value: getValue(state, `${object.id}.${prop.name}`),
        initial: formattedValue,
      }
    } else {
      result[prop.name] = formattedValue
    }
  }

  for (const child of normalChildComponents) {
    const childValues = values[child.name] || {}
    const childGlobals = libraryGlobals[child.name] || {}

    result[child.name] = {}

    for (const prop of child.props) {
      let value = prop.global ? childGlobals[prop.name] : childValues[prop.name]
      let binding = null

      if (value) {
        binding = value

        value = evaluateBinding(state, value, { ...context, getBinding })

        // If condition, evaluate bindings for comparison values and evaluateCondition
        if (prop.type === 'condition') {
          value = getConditionValue(value, binding, {
            ...context,
            getBinding,
            state,
          })
        }

        if (prop.type === 'object' && typeof binding === 'object' && !value) {
          value = binding
        }
      }

      if (hasRole(prop, 'formValue')) {
        result[child.name][prop.name] = {
          value: getValue(state, `${object.id}.${child.name}.${prop.name}`),
          initial: formatLibraryValue(value, prop, binding, {
            getAssetURL,
            getImageUploadsBaseURL,
            getFileUploadsBaseURL,
            values: childValues,
            object,
          }),
        }
      } else {
        result[child.name][prop.name] = formatLibraryValue(
          value,
          prop,
          binding,
          {
            getAssetURL,
            getImageUploadsBaseURL,
            getFileUploadsBaseURL,
            values: childValues,
            object,
          }
        )
      }
    }
  }

  return {
    bindingValues,
    componentProps: result,
  }
}

export const hasRole = (prop, role) => {
  if (Array.isArray(prop.role)) {
    return prop.role.includes(role)
  } else {
    return prop.role === role
  }
}
